import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { apiErr } from "@/lib/api";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Sesión iniciada");
      navigate("/app");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between bg-[#0F172A] p-12 text-white">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-blue-600 grid place-items-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <div className="font-display font-bold text-lg notranslate" translate="no">CHRONOVIA</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-widest">
              Facturación
            </div>
          </div>
        </Link>
        <div>
          <h2 className="font-display text-3xl font-bold leading-tight">
            "Antes tardaba una hora en hacer una factura. Ahora la envío en 30
            segundos y ya está en mi gestoría."
          </h2>
          <p className="mt-6 text-slate-400 text-sm">
            — María L., diseñadora freelance
          </p>
        </div>
        <div className="text-xs text-slate-500">
          © {new Date().getFullYear()} <span className="notranslate" translate="no">CHRONOVIA</span> · Hecho en España
          <br />
          <a href="mailto:info@autonomopro.online" className="text-slate-400 hover:text-slate-200 transition">
            info@autonomopro.online
          </a>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">
            Bienvenido de nuevo
          </h1>
          <p className="text-slate-500 mt-2">
            Accede a tu panel de facturación.
          </p>

          <form className="mt-8 space-y-4" onSubmit={submit}>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tucorreo@ejemplo.com"
                data-testid="login-email-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
                className="mt-1.5"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 h-11"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? "Entrando..." : "Entrar"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-500">
            ¿Aún no tienes cuenta?{" "}
            <Link
              to="/register"
              className="text-blue-600 hover:underline font-medium"
              data-testid="login-register-link"
            >
              Regístrate gratis
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
